import React, { useState, useEffect, useCallback } from 'react';
import { ref, get, remove } from 'firebase/database';
import { db } from './firebase-config';
import ApplicationModal from './ApplicationModal';
import './ApplicationsList.css';

function ApplicationsList() {
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'artist', 'media', 'brand'
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Load applications from database
  const loadApplications = useCallback(async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const applicationsRef = ref(db, 'applications');
      const snapshot = await get(applicationsRef);
      
      if (snapshot.exists()) {
        const applicationsData = snapshot.val();
        const applicationsList = Object.keys(applicationsData).map(key => ({
          id: key,
          ...applicationsData[key]
        }));
        
        // Sort by creation date (newest first)
        applicationsList.sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        
        setApplications(applicationsList);
      } else {
        setApplications([]);
      }
    } catch (err) {
      console.error('Error loading applications:', err);
      setError('Failed to load applications. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load applications on component mount
  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  // Delete application
  const deleteApplication = async (application) => {
    if (window.confirm(`Are you sure you want to delete this application from ${application.name}?`)) {
      try {
        await remove(ref(db, `applications/${application.id}`));
        
        // Update local state
        setApplications(prev => prev.filter(app => app.id !== application.id));
        
        setSuccess('Application deleted successfully.');
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        console.error('Error deleting application:', err);
        setError('Failed to delete application. Please try again.');
        
        // Clear error message after 3 seconds
        setTimeout(() => setError(''), 3000);
      }
    }
  };

  // Filter applications
  const filteredApplications = applications.filter(application => {
    if (filter === 'all') return true;
    return application.type === filter;
  });

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get application type label
  const getTypeLabel = (type) => {
    switch (type) {
      case 'artist': return 'Music Artist';
      case 'media': return 'Media Outlet';
      case 'brand': return 'Brand Partner';
      default: return type;
    }
  };

  // View application details
  const viewApplicationDetails = (application) => {
    setSelectedApplication(application);
  };

  // Close modal
  const closeModal = () => {
    setSelectedApplication(null);
  };

  return (
    <div className="applications-list">
      <h2 className="applications-list__title">Applications</h2>
      
      <div className="applications-list__filters">
        <button 
          className={`applications-list__filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({applications.length})
        </button>
        <button 
          className={`applications-list__filter-btn ${filter === 'artist' ? 'active' : ''}`}
          onClick={() => setFilter('artist')}
        >
          Artists ({applications.filter(a => a.type === 'artist').length})
        </button>
        <button 
          className={`applications-list__filter-btn ${filter === 'media' ? 'active' : ''}`}
          onClick={() => setFilter('media')}
        >
          Media ({applications.filter(a => a.type === 'media').length})
        </button>
        <button 
          className={`applications-list__filter-btn ${filter === 'brand' ? 'active' : ''}`}
          onClick={() => setFilter('brand')}
        >
          Brands ({applications.filter(a => a.type === 'brand').length})
        </button>
      </div>
      
      {error && <p className="applications-list__error">{error}</p>}
      {success && <p className="applications-list__success">{success}</p>}
      
      {isLoading ? (
        <p className="applications-list__loading">Loading applications...</p>
      ) : filteredApplications.length > 0 ? (
        <div className="applications-list__table-container">
          <table className="applications-list__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Type</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.map(application => (
                <tr key={application.id}>
                  <td>{application.name}</td>
                  <td>
                    <a href={`mailto:${application.email}`} className="applications-list__email-link">
                      {application.email}
                    </a>
                  </td>
                  <td>
                    <span className={`type-badge type-${application.type}`}>
                      {getTypeLabel(application.type)}
                    </span>
                  </td>
                  <td>{formatDate(application.createdAt)}</td>
                  <td className="applications-list__actions">
                    <button 
                      className="applications-list__action-btn view"
                      onClick={() => viewApplicationDetails(application)}
                      title="View Details"
                    >
                      Details
                    </button>
                    <button 
                      className="applications-list__action-btn delete"
                      onClick={() => deleteApplication(application)}
                      title="Delete"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="applications-list__empty">No applications found.</p>
      )}
      
      {selectedApplication && (
        <ApplicationModal 
          application={selectedApplication} 
          onClose={closeModal} 
        />
      )}
    </div>
  );
}

export default ApplicationsList; 